import View from '@play-co/timestep-core/lib/ui/View';
import { Feature } from 'src/replicant/analytics';
import { ImageScaleView, animate } from '@play-co/timestep-core/ui';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import {
  createWaiter,
  getScreenDimensions,
  getScreenTop,
  waitForItPromise,
} from 'src/lib/utils';
import StateObserver, { now, user } from 'src/StateObserver';
import {
  match2BundleSaleGetCurrentEventItems,
  match2BundleSaleGetCurrentSchedule,
  match2BundleSaleGetPurchasedItems,
  match2BundleSaleGetRemainingTime,
  match2BundleSaleIsFreeProduct,
} from 'src/replicant/match2/bundlesale/match2-bundlesale.getters';
import { Match2BundleSalePack } from './Match2BundleSalePack';
import { BundleSaleProductId } from 'src/replicant/match2/bundlesale/match2-bundlesale.ruleset';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import { ProductId } from 'src/replicant/iap';
import { startPurchaseFlow } from 'src/lib/iap/iap.util';
import { match2BundleSaleCompletePurchase } from './match2-bundlesale.utils';
import { duration } from 'src/replicant/utils/duration';
import { Match2BundleSaleArrow } from './Match2BundleSaleArrow';
import { Match2Timer } from '../components/Match2Timer';
import { Match2BundleSaleSnow } from './Match2BundleSaleSnow';
import {
  bundleSaleTheme,
  getBundleSaleAssetPaths,
} from './match2-bundlesale.theme';
import { createPopupDeeplink } from 'src/game/launch/deepLinks';
import { createEmitter } from 'src/lib/Emitter';

type CloseHandler = (result: 'success' | 'failed' | 'close') => void;
export default class PopupMatch2BundleSale extends PopupBasicLazy {
  private content: View;
  private shade: View;
  private header: ImageScaleView;
  private eventTitle: ImageScaleView;
  private timer: Match2Timer;
  private info: LangBitmapFontTextView;
  private close: CloseHandler;
  private packs: Match2BundleSalePack[] = [];
  private arrows: Match2BundleSaleArrow[] = [];
  private waiter?: ReturnType<typeof createWaiter>;
  private snow?: Match2BundleSaleSnow;
  private maxPacks = 6;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => {
        // The close button is not in its original parent in this popup
        // So we make sure we disable it to avoid double clicks
        this.buttonClose.setDisabled(true);
        opts.close('close');
      },
      tracking: {
        feature: Feature.MATCH2.BUNDLE_SALE._,
        $subFeature: Feature.MATCH2.BUNDLE_SALE.POPUP,
      },
    });
    this.box.removeFromSuperview();

    this.close = opts.close;

    this.shade = new View({
      superview: this.root,
      width: opts.superview.style.width,
      height: getScreenDimensions().height,
      x: this.root.style.width * 0.5,
      y: this.root.style.height * 0.5,
      centerOnOrigin: true,
      backgroundColor: '#471843',
      opacity: 0.7,
    });

    this.content = new View({
      superview: this.root,
      width: this.root.style.width,
      height: this.root.style.height,
      infinite: true,
    });

    this.header = new ImageScaleView({
      superview: this.content,
      image: bundleSaleTheme().getAsset('header'),
      width: 720,
      height: 457,
      x: 0,
      y: getScreenTop(),
      scaleMethod: 'cover',
      ...bundleSaleTheme().getStyle('header'),
    });

    if (bundleSaleTheme().getAsset('snowflake')) {
      this.snow = new Match2BundleSaleSnow({
        superview: this.content,
        width: this.content.style.width,
        height: this.content.style.height,
      });
    }

    this.timer = new Match2Timer({
      superview: this.header,
      centerOnOrigin: true,
      x: this.header.style.width * 0.5,
      y: 150,
      ...bundleSaleTheme().getStyle('timer'),
    });
    this.timer.setRemainingTimeGetter(match2BundleSaleGetRemainingTime);

    this.info = new LangBitmapFontTextView({
      superview: this.header,
      x: this.header.style.width * 0.5,
      y: 270,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 36,
      font: 'Title',
      width: 350,
      height: 80,
      color: '#FFFFFF',
      localeText: () => i18n('match2.bundleSale.info'),
      ...bundleSaleTheme().getStyle('info'),
    });

    this.eventTitle = new ImageScaleView({
      superview: this.header,
      width: 514,
      height: 100,
      x: this.header.style.width * 0.5,
      y: 65,
      centerOnOrigin: true,
      scaleMethod: 'contain',
    });

    this.buttonClose.updateOpts({
      superview: this.content,
      x: this.root.style.width - 50,
      y: this.header.style.y + 75,
    });

    // The popup is assembled upfront, and should be hidden until animateShow is called
    this.content.updateOpts({ visible: false });

    createEmitter(this.root, ({ ui }) => ui.screenSize).addListener(
      (screen) => {
        this.shade.updateOpts({
          height: screen.height,
          y: getScreenTop() + screen.height * 0.5,
          centerOnOrigin: true,
        });
        this.header.updateOpts({
          y: getScreenTop(),
        });
        this.buttonClose.updateOpts({
          y: this.header.style.y + 75,
        });
      },
    );
  }

  public getAssetsGroup(): AssetGroup | null {
    return new AssetGroup(getBundleSaleAssetPaths());
  }

  public async init() {
    super.init({});

    const locale = StateObserver.getLocale();
    this.eventTitle.updateOpts({
      image: bundleSaleTheme().getAsset(`title_${locale}`),
      ...bundleSaleTheme().getStyle(`title_${locale}`),
      centerOnOrigin: true,
    });

    this.mountArrows();
    this.mountPacks();
    this.refreshPacks();
  }

  public override async fadeIn() {
    this.snow?.start();
    this.autoUpdate();
    await super.fadeIn();
    await this.animateShow();
  }

  public override async fadeOut() {
    this.snow?.stop();
    this.stopAutoUpdate();
    await this.animateHide();
    super.fadeOut();
  }

  public autoUpdate() {
    this.waiter?.clear();
    this.waiter = createWaiter();
    this.waiter.wait(this.autoUpdate.bind(this), duration({ seconds: 1 }));
    this.update();
  }

  public stopAutoUpdate() {
    this.waiter?.clear();
    this.waiter = undefined;
  }

  public update() {
    this.timer.update();
    const event = match2BundleSaleGetCurrentSchedule(now());
    const endAt = event?.endTime || 0;
    // Auto close the popup if event has ended during the session
    if (now() >= endAt) this.close('close');
  }

  private getPositionByIndex(index: number) {
    const spacing = 305;
    const offsetX = 200;
    const offsetY = 500;
    const columns = 2;
    const row = Math.floor(index / columns);
    const col = row % 2 ? 1 - (index % columns) : index % columns;
    return {
      x: offsetX + spacing * col,
      y: offsetY + spacing * row,
    };
  }

  private mountArrows() {
    for (let i = 0; i < this.maxPacks - 1; i++) {
      const nextPosition = this.getPositionByIndex(i + 1);
      const position = this.getPositionByIndex(i);
      const pipe = new Match2BundleSaleArrow({
        superview: this.content,
        ...position,
      });
      pipe.updateArrow(nextPosition);
      this.arrows.push(pipe);
    }
  }

  private mountPacks() {
    for (let i = 0; i < this.maxPacks; i++) {
      this.spawnPack(false);
    }
  }

  /**
   * Spawn the next product pack available
   */
  private async spawnPack(animated: boolean) {
    const allItems = match2BundleSaleGetCurrentEventItems(user(), now());
    const excludeItems = [
      ...match2BundleSaleGetPurchasedItems(user(), now()), // Exclude already purchased packs
      ...this.packs.map((pack) => pack.productId), // Exclude already spawned packs
    ];
    // Filter out items that are already spawned or purchased
    const remainingItems = allItems.filter(
      (item) => !excludeItems.includes(item),
    );

    // The first item  of the remaining list should be the next spawn
    const item = remainingItems[0];

    // Skip if there are no items left
    if (!item) return;

    // The pack should spawn at the end of the list
    const index = this.packs.length;
    const position = this.getPositionByIndex(index);
    const pack = new Match2BundleSalePack({
      superview: this.content,
      ...position,
    });
    pack.setup({
      productId: item,
      onPurchase: () => this.buyProduct(item),
    });
    this.packs.push(pack);
    if (animated) await pack.animateShow();
  }

  /**
   * Dismiss first pack (only if purchased) and move all the others to their new position
   */
  private async moveProducts() {
    const purchased = match2BundleSaleGetPurchasedItems(user(), now());
    const firstPack = this.packs[0];

    // Check if first product has been purchased, if not, there is no need to move
    if (!purchased.includes(firstPack.productId)) return;

    // Refresh the first pack
    firstPack.refresh();

    // Wait the claim popup close
    await waitForItPromise(200);

    // Hide and pop out the first pack
    await firstPack.animateHide();
    this.packs.splice(0, 1);

    // Move all the other packs to their new position
    for (let i = 0; i < this.packs.length; i++) {
      const pack = this.packs[i];
      const position = this.getPositionByIndex(i);
      pack.animateMove(position);
      // Wait a little bit before moving the next pack
      await waitForItPromise(150);
    }

    // Spawn a new pack at the end
    await this.spawnPack(true);

    // Update arrows
    this.updateArrows();

    // Refresh all packs
    this.refreshPacks();

    // Animate the unlocked pack to attract attention
    this.packs[0]?.animateWobble();
  }

  private refreshPacks() {
    for (const pack of this.packs) {
      pack.refresh();
    }

    // If there are no more packs, set as complete
    if (!this.packs.length) {
      this.setAsComplete();
    }
  }

  private async buyProduct(productId: BundleSaleProductId) {
    sounds.sfx.play('ok');

    // If it's a free product, just proceed with rewards
    if (match2BundleSaleIsFreeProduct(productId)) {
      await StateObserver.invoke.match2BundleSalePurchase({
        product: productId,
      });
      await match2BundleSaleCompletePurchase(productId);
      void this.moveProducts();
      return;
    }

    // Cast id to paid product id - tests are ensuring that this is safe
    const paidProductId = productId as ProductId;

    await startPurchaseFlow({
      productId: paidProductId,
      feature: 'sale',
      subFeature: 'sale_endless',
      onComplete: () => {
        void this.moveProducts();
      },
      context: [createPopupDeeplink('popupMatch2BundleSale', {})],
    });
  }

  private async animateShow() {
    this.content.updateOpts({ visible: true });

    const shadeOpacity = this.shade.style.opacity;
    const headerY = this.header.style.y;
    const infoY = this.info.style.y;

    this.header.updateOpts({ y: headerY - 400 });
    this.eventTitle.updateOpts({ scale: 0 });
    this.timer.updateOpts({ scale: 0 });
    this.info.updateOpts({ opacity: 0 });
    this.shade.updateOpts({ opacity: 0 });
    this.info.updateOpts({ y: infoY + 30, opacity: 0 });

    for (const pack of this.packs) pack.updateOpts({ scale: 0 });

    animate(this.shade).now({ opacity: shadeOpacity }, 500, animate.linear);

    animate(this.header).now({ y: headerY }, 300, animate.easeOut).toPromise();

    await animate(this.eventTitle)
      .now({ scale: 1 }, 300, animate.easeOutBack)
      .toPromise();

    await animate(this.timer)
      .now({ scale: 1 }, 300, animate.easeOutBack)
      .toPromise();

    animate(this.info)
      .now({ y: infoY, opacity: 1 }, 300, animate.easeOutBack)
      .toPromise();

    for (let i = 0; i < this.packs.length; i++) {
      const pack = this.packs[i];
      pack.animateShow();
      await waitForItPromise(100);
    }

    void this.updateArrows();
  }

  private async animateHide() {
    await animate(this.getView())
      .now({ opacity: 0 }, 300, animate.linear)
      .toPromise();
  }

  /**
   * Update arrows that are linking packs, and hide the ones that are not needed
   */
  private async updateArrows() {
    for (let i = 0; i < this.arrows.length; i++) {
      const arrow = this.arrows[i];
      if (this.packs.length === 6 || i < this.packs.length - 1) {
        void arrow.animateShow();
      } else {
        void arrow.animateHide();
      }
    }
  }

  /**
   * To be called when all products have been purchased
   * Show all purchased products and a congrats message
   */
  private async setAsComplete() {
    this.info.localeText = () => i18n('match2.bundleSale.complete');
    const allItems = match2BundleSaleGetCurrentEventItems(user(), now());
    const purchased = match2BundleSaleGetPurchasedItems(user(), now());
    const items = purchased.filter((item) => allItems.includes(item));
    const cols = 7;
    const spacing = 100;
    const offsetX = 58;
    const offsetY = 400;
    for (let i = 0; i < items.length; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = col * spacing + offsetX;
      const y = row * spacing + offsetY;
      const item = items[i];
      const pack = new Match2BundleSalePack({
        superview: this.content,
        scale: 0.35,
        x,
        y,
      });
      pack.setup({
        productId: item,
        onPurchase: () => {},
      });
      pack.animateShow();
      await waitForItPromise(20);
    }
  }
}
